import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useLang } from "@/context/LanguageContext";
import { formatEUR } from "@/lib/i18n";

const KPI_SERIES = [
  { key: "totale_attivo", label_it: "Totale Attivo", label_en: "Total Assets", color: "#1D4ED8", fmt: "eur" },
  { key: "patrimonio_netto", label_it: "Patrimonio Netto", label_en: "Equity", color: "#15803D", fmt: "eur" },
  { key: "ricavi", label_it: "Ricavi", label_en: "Revenue", color: "#0EA5E9", fmt: "eur" },
  { key: "utile_esercizio", label_it: "Utile d'esercizio", label_en: "Net Income", color: "#F59E0B", fmt: "eur" },
];

const RATIO_SERIES = [
  { key: "roe", label_it: "ROE %", label_en: "ROE %", color: "#1D4ED8" },
  { key: "roi", label_it: "ROI %", label_en: "ROI %", color: "#15803D" },
  { key: "current_ratio", label_it: "Current Ratio", label_en: "Current Ratio", color: "#0EA5E9" },
  { key: "debt_ratio", label_it: "Indebitamento", label_en: "Debt/Equity", color: "#B91C1C" },
];

export default function TrendChart({ history }) {
  const { t, lang } = useLang();
  const [mode, setMode] = useState("kpi"); // kpi | ratios | altman

  const data = useMemo(() => {
    if (!history || !history.length) return [];
    return history.map((h) => {
      const row = { label: h.label };
      for (const s of KPI_SERIES) {
        row[s.key] = h.totals?.[s.key] ?? null;
      }
      const rmap = {};
      for (const r of (h.ratios || [])) rmap[r.key] = r.value;
      for (const s of RATIO_SERIES) row[s.key] = rmap[s.key] ?? null;
      row.altman = h.altman?.score ?? null;
      return row;
    });
  }, [history]);

  if (!history || history.length < 2) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        {t("trend_no_data")}
      </div>
    );
  }

  const activeSeries =
    mode === "kpi" ? KPI_SERIES :
    mode === "ratios" ? RATIO_SERIES :
    [{ key: "altman", label_it: "Altman Z'", label_en: "Altman Z'", color: "#1D4ED8" }];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="trend-chart">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading text-sm font-semibold tracking-tight text-slate-900">
            {t("trend_title")} · {history.length} {lang === "it" ? "esercizi" : "years"}
          </div>
          <div className="text-xs text-slate-500">{t("trend_subtitle")}</div>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 text-xs">
          {[
            { k: "kpi", l: "KPI" },
            { k: "ratios", l: lang === "it" ? "Indici" : "Ratios" },
            { k: "altman", l: "Altman" },
          ].map((tab) => (
            <button
              key={tab.k}
              data-testid={`trend-mode-${tab.k}`}
              onClick={() => setMode(tab.k)}
              className={`rounded-full px-3 py-1 font-medium transition-colors ${mode === tab.k ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"}`}
            >
              {tab.l}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 8, right: 20, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis
            tick={{ fontSize: 11, fill: "#475569" }}
            tickLine={false}
            axisLine={{ stroke: "#E2E8F0" }}
            tickFormatter={(v) => (mode === "kpi" ? (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v) : v)}
          />
          <Tooltip
            contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
            formatter={(value, name) => {
              if (mode === "kpi") return [formatEUR(value, lang), name];
              return [value ?? "—", name];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {activeSeries.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={lang === "it" ? s.label_it : s.label_en}
              stroke={s.color}
              strokeWidth={2.25}
              dot={{ r: 4, fill: s.color }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
